import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, 
  MessageCircle, 
  Menu, 
  X,
  ArrowUpRight,
  LayoutGrid,
  Zap,
  ShieldCheck
} from 'lucide-react';
import { SERVICES } from './constants';

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          } else {
            entry.target.classList.remove('active');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const phone1 = "+966 50 723 9829";
  const phone2 = "+966 58 071 8779";

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => {
      const newState = !prev;
      if (newState) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
      return newState;
    });
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      if (isMenuOpen) toggleMenu();
      const offset = 80; // height of the fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: id === 'hero' ? 0 : offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const menuItems = [
    { label: 'الرئيسية', id: 'hero' },
    { label: 'من نحن', id: 'about' },
    { label: 'خدماتنا', id: 'services' },
    { label: 'تواصل معنا', id: 'contact' }
  ];

  return (
    <div className="min-h-screen selection:bg-blue-600 selection:text-white overflow-x-hidden">
      {/* Navigation Bar */}
      <nav className={`fixed w-full z-[90] transition-all duration-700 ${scrolled ? 'py-4 bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm' : 'py-10 bg-transparent'}`}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex justify-between items-center">
          <div className="flex flex-col group cursor-pointer" onClick={(e) => scrollToSection(e as any, 'hero')}>
            <span className={`text-2xl md:text-3xl font-bold tracking-tight transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              أمـ فال <span className="text-blue-600 font-light">العقارية</span>
            </span>
            <div className="h-4 overflow-hidden mt-1">
               <span className={`text-[10px] uppercase tracking-[0.4em] font-medium block transition-all duration-500 ${scrolled ? 'text-blue-500 translate-y-0' : 'text-white/40 translate-y-full opacity-0'}`}>
                ثقتكم محل اهتمامنا
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-10">
            <div className={`hidden lg:flex items-center gap-10 text-[13px] font-semibold uppercase tracking-wider transition-colors duration-500 ${scrolled ? 'text-slate-600' : 'text-white/80'}`}>
              {menuItems.slice(1).map((item) => (
                <a 
                  key={item.id} 
                  href={`#${item.id}`} 
                  onClick={(e) => scrollToSection(e, item.id)}
                  className="hover:text-blue-600 transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </div>
            
            <button 
              onClick={toggleMenu}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 group border ${scrolled ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white/10 border-white/20 text-white hover:bg-white hover:text-slate-900'}`}
              aria-label="Toggle Menu"
            >
              <Menu className={`w-5 h-5 transition-all duration-500 ${isMenuOpen ? 'rotate-90 scale-0 opacity-0' : 'opacity-100 scale-100'}`} />
              <X className={`w-5 h-5 absolute transition-all duration-500 ${isMenuOpen ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-0 opacity-0'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Modern Overlay Menu */}
      <div className={`fixed inset-0 z-[100] bg-white flex flex-col transition-all duration-[800ms] cubic-bezier(0.19, 1, 0.22, 1) ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}>
        <div className="p-8 lg:p-12 flex justify-between items-center border-b border-slate-50">
          <span className="text-slate-900 text-2xl font-bold tracking-tight">أمـ فال</span>
          <button 
            onClick={toggleMenu} 
            className="w-14 h-14 rounded-full border border-slate-200 flex items-center justify-center text-slate-900 hover:bg-slate-950 hover:text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 flex flex-col justify-center items-center px-8">
          <div className="flex flex-col space-y-6 text-center">
            {menuItems.map((item, idx) => (
              <a 
                key={idx} 
                href={`#${item.id}`} 
                onClick={(e) => scrollToSection(e, item.id)}
                className="text-slate-900 text-[clamp(2.5rem,7vw,5rem)] font-bold hover:text-blue-600 transition-all transform hover:scale-110 leading-tight tracking-tight w-fit mx-auto"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
        <div className="p-12 border-t border-slate-50 flex flex-col items-center gap-6">
           <p className="text-[10px] text-slate-300 font-bold tracking-[0.5em] uppercase">ثقتكم محل اهتمامنا</p>
        </div>
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen flex items-center overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1582407947304-fd86f028f716?q=80&w=2196&auto=format&fit=crop" 
            className="w-full h-full object-cover opacity-50"
            alt="Real Estate Concept"
            style={{ animation: 'slow-zoom 30s ease-in-out infinite' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-transparent to-slate-950"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 pt-20">
          <div className="max-w-4xl space-y-10">
            <div className="animate-fade-up flex items-center gap-6">
              <div className="h-[1px] w-16 bg-blue-500"></div>
              <span className="text-blue-400 font-semibold uppercase tracking-[0.4em] text-[12px]">ثقتكم محل اهتمامنا</span>
            </div>
            
            <h1 className="animate-fade-up text-white text-[clamp(3.5rem,10vw,8.5rem)] font-bold leading-[1.2] tracking-tight" style={{ animationDelay: '200ms' }}>
              الاحترافية في <br/> <span className="text-blue-600">إدارة العقار</span>
            </h1>
            
            <p className="animate-fade-up text-white/70 text-xl md:text-2xl font-normal leading-relaxed max-w-2xl" style={{ animationDelay: '400ms' }}>
              نحن في أمـ فال نجمع بين الخبرة المحلية والحلول التقنية العالمية لنقدم لكم أفضل تجربة في المقاولات، الوساطة، وإدارة الأملاك.
            </p>
            
            <div className="animate-fade-up flex flex-wrap gap-6 pt-6" style={{ animationDelay: '600ms' }}>
               <a 
                href="#services" 
                onClick={(e) => scrollToSection(e, 'services')}
                className="px-12 py-6 bg-white text-slate-950 rounded-full font-bold text-xl hover:bg-blue-600 hover:text-white transition-all shadow-2xl shadow-blue-500/10"
               >
                  استكشف خدماتنا
               </a>
               <a 
                href="#contact" 
                onClick={(e) => scrollToSection(e, 'contact')}
                className="px-12 py-6 bg-transparent border border-white/20 text-white rounded-full font-bold text-xl hover:bg-white/10 transition-all"
               >
                  تواصل معنا
               </a>
            </div>
          </div>
        </div>
      </section>

      {/* Identity Section */}
      <section id="about" className="py-24 lg:py-40 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center reveal space-y-10">
            <div className="space-y-4">
              <span className="text-blue-600 font-bold text-xs uppercase tracking-widest block">هوية أمـ فال</span>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 leading-[1.3]">
                النزاهة هي <br/> <span className="text-blue-600">حجر الأساس</span>
              </h2>
            </div>
            <p className="text-slate-500 text-lg md:text-xl font-normal leading-loose">
              منذ انطلاقتنا، وضعنا "الثقة" كمعيار أول لكافة عملياتنا. في أمـ فال، لا نقوم فقط ببيع أو إدارة العقارات، بل نبني علاقات متينة تضمن لمستثمرينا وعملائنا أعلى درجات الأمان والربحية.
            </p>
            <div className="flex justify-center gap-10 pt-4">
               <div className="space-y-2">
                 <p className="text-5xl font-bold text-slate-900 tracking-tighter">100%</p>
                 <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest">التزام مهني</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-24 lg:py-40 bg-slate-50">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-24 reveal">
            <span className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-4 block">نطاق عملنا</span>
            <h2 className="text-4xl md:text-6xl font-bold text-slate-900 tracking-tight">حلول <span className="text-blue-600 italic">مبتكرة</span></h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {SERVICES.map((service, idx) => (
              <div 
                key={service.id}
                className={`reveal group p-10 lg:p-14 rounded-[2.5rem] transition-all duration-500 cursor-default bg-white border border-slate-100 hover:shadow-3xl hover:-translate-y-3 ${idx === 1 ? 'lg:translate-y-8' : ''}`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <div className="mb-12 flex justify-between items-start">
                  <div className="w-20 h-20 rounded-3xl bg-blue-50 flex items-center justify-center transition-colors group-hover:bg-blue-600">
                    {idx === 0 ? <Zap className="text-blue-600 group-hover:text-white w-8 h-8" /> : 
                     idx === 1 ? <LayoutGrid className="text-blue-600 group-hover:text-white w-8 h-8" /> : 
                     <ShieldCheck className="text-blue-600 group-hover:text-white w-8 h-8" />}
                  </div>
                  <span className="text-slate-100 text-6xl font-bold transition-colors group-hover:text-blue-50/50">0{idx + 1}</span>
                </div>
                <h4 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6 leading-relaxed">{service.title}</h4>
                <p className="text-slate-500 text-lg font-normal leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 lg:py-40 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600 rounded-full blur-[300px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-900 rounded-full blur-[250px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-32 items-center">
            <div className="reveal space-y-12">
              <h2 className="text-5xl md:text-8xl font-bold leading-[1.3] tracking-tight">
                لنخطط <br/> <span className="text-blue-600 italic">لنجاحك.</span>
              </h2>
              <p className="text-white/40 text-xl md:text-2xl font-light leading-loose max-w-xl">
                نحن هنا للإجابة على كافة استفساراتكم وتقديم المشورة الفنية اللازمة لضمان أفضل استثمار لعقاركم.
              </p>
              
              <div className="flex flex-wrap gap-8">
                <a href={`tel:${phone1.replace(/\s/g, '')}`} className="flex items-center gap-6 bg-white text-slate-950 px-14 py-7 rounded-full font-bold text-xl hover:bg-blue-600 hover:text-white transition-all">
                  <Phone className="w-6 h-6" />
                  <span>اتصال مباشر</span>
                </a>
                <a href={`https://wa.me/${phone1.replace(/[+\s]/g, '')}`} className="flex items-center gap-6 bg-white/5 border border-white/20 px-14 py-7 rounded-full font-bold text-xl hover:bg-white/10 transition-all">
                  <MessageCircle className="w-6 h-6 text-green-500" />
                  <span>عبر واتساب</span>
                </a>
              </div>
            </div>

            <div className="reveal space-y-8 text-right" style={{ transitionDelay: '300ms' }}>
              {[
                { label: 'إدارة الأملاك والتشغيل', num: phone1 },
                { label: 'المبيعات والتسويق العقاري', num: phone2 }
              ].map((item, i) => (
                <div 
                  key={i}
                  className="group p-12 border border-white/10 rounded-[2.5rem] hover:bg-white/5 transition-all cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-blue-600/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                  <p className="text-blue-500 font-bold uppercase tracking-widest text-xs mb-6 relative z-10">{item.label}</p>
                  <div className="flex justify-between items-center relative z-10">
                    <p className="text-4xl md:text-5xl font-bold ltr tracking-tight group-hover:text-blue-400 transition-colors">{item.num}</p>
                    <ArrowUpRight className="w-10 h-10 text-white/20 group-hover:text-blue-500 group-hover:translate-x-2 group-hover:-translate-y-2 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-24 bg-white border-t border-slate-50">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-20 mb-24">
            <div className="flex flex-col gap-8 max-w-md">
              <span className="text-3xl font-bold tracking-tight text-slate-900">أمـ فال <span className="text-blue-600">العقارية</span></span>
              <p className="text-slate-400 text-lg leading-relaxed font-normal">بنينا سمعتنا على الثقة والالتزام، ونواصل التميز في تقديم الحلول العقارية المتكاملة والمقاولات في المملكة العربية السعودية.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-24 text-right">
              <div className="space-y-8">
                <p className="text-slate-900 font-bold uppercase tracking-widest text-xs">تصفح</p>
                <ul className="space-y-4 text-slate-500 text-lg font-medium">
                  {menuItems.map((item) => (
                    <li key={item.id}>
                      <a 
                        href={`#${item.id}`} 
                        onClick={(e) => scrollToSection(e, item.id)}
                        className="hover:text-blue-600 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          <div className="pt-12 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-8 text-slate-400 text-xs font-bold uppercase tracking-[0.4em]">
            <p>© 2026 شركة أمـ فال العقارية والمقاولات. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Action Button */}
      <a 
        href={`https://wa.me/${phone1.replace(/[+\s]/g, '')}`}
        target="_blank" 
        rel="noreferrer"
        className="fixed bottom-10 right-10 w-16 h-16 bg-blue-600 text-white rounded-full shadow-2xl shadow-blue-500/30 flex items-center justify-center hover:scale-110 active:scale-95 transition-all z-[80]"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-8 h-8" />
      </a>
    </div>
  );
};

export default App;